import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "app/core/services/admin/auth/auth.service";
import { DataService } from "app/core/services/global/data/data.service";
import { LayoutService } from "app/core/services/global/layout/layout.service";
import { SwalModalService } from "app/core/services/global/modal/modal.service";
import { ToastService } from "app/core/services/global/toast/toast.service";
import { ElementTypeService } from "app/core/services/pim/element-type.service";
import { ListCascadeService } from "app/core/services/pim/listcascade.service";
import { ActionButtonsDef, ActionEvent, ActionType } from "app/shared/components/action-buttons";
import { ACL } from "app/shared/models/acl";
import { TableDataPipe } from "app/shared/pipes/table-data.pipe";
import { Subscription } from "rxjs";

@Component({
    selector: "app-listcascade-list",
    templateUrl: "./listcascade-list.component.html",
})
export class ListCascadeListComponent implements OnInit, OnDestroy {
    table: [string, string][];
    columns: string[];

    acl: ACL;

    _lists: any;

    private actionButtonSource: Subscription;

    private _searchSubscription: Subscription;

    readonly actions: ActionButtonsDef = [];

    private importButtonSource: Subscription;

    dataModels = [];

    constructor(
        private _modalService: SwalModalService,
        private _translateService: TranslateService,
        private _router: Router,
        private _route: ActivatedRoute,
        private _layout: LayoutService,
        private _tableDataPipe: TableDataPipe,
        private _authService: AuthService,
        private _listcascadeService: ListCascadeService,
        private _dataService: DataService,
        private _toastService: ToastService,
        private _elementTypeService: ElementTypeService
    ) {}
    ngOnInit(): void {
        this.acl = this._authService.getUserAclFromToken();

        if (this.acl.SYSPAD_PIM_LIST_CREATE) {
            this._layout.actionButton.enable = true;
            this._layout.actionButton.title = "pim.listscascade.create";
            this.actionButtonSource = this._layout.actionButton.click$.subscribe((_) => this.onCreate());

            this._layout.importButton.enable = true;
            this._layout.importButton.title = "general.import";
            this.importButtonSource = this._layout.importButton.click$.subscribe((_) => this.onImport());
        }
        if (this.acl.SYSPAD_PIM_LIST_UPDATE) {
            this.actions.push({ type: ActionType.Update, right: "SYSPAD_PIM_LIST_UPDATE" });
        }

        if (this.acl.SYSPAD_PIM_LIST_DELETE) {
            this.actions.push({ type: ActionType.Delete, right: "SYSPAD_PIM_LIST_DELETE" });
        }

        this._lists = this._route.snapshot.data.lists.data;

        this._initList(this._lists);

        this._elementTypeService.getElementTypes().subscribe((res: any) => {
            this.dataModels = res.data;
        });
    }

    ngOnDestroy(): void {
        if (this.actionButtonSource) {
            this.actionButtonSource.unsubscribe();
        }

        this._layout.actionButton.enable = false;
        this._layout.actionButton.title = null;

        if (this.importButtonSource) {
            this.importButtonSource.unsubscribe();
        }

        this._layout.importButton.enable = false;
        this._layout.importButton.title = null;
    }

    onCreate() {
        this._openCreateModal();
    }

    private _openCreateModal() {
        const name = this._translateService.instant("general.name");
        const associatedModelLabel = this._translateService.instant("general.associatedModel");

        return this._modalService
            .open({
                title: this._translateService.instant("breadcrumb.pim.listscascade.create"),
                showCloseButton: true,
                confirmButtonText: this.acl.SYSPAD_PIM_LIST_UPDATE ? this._translateService.instant("general.create_open") : this._translateService.instant("general.create"),
                confirmButtonColor: "$maestro",
                showCancelButton: true,
                cancelButtonText: this._translateService.instant("general.cancel"),
                cancelButtonColor: "#FF0000",
                html: `<div class="form-group">
                        <label for="ModalListTitle" translate>${name}</label>
                        <input class="form-control" type="text" placeholder="" id="ModalListTitle">
                        <br>
                        <label for="ModalListModel" translate>${associatedModelLabel}</label>
                        <select class="form-control" id="ModalListModel">
                            <option></option>
                        </select>
                        </div>`,
                didOpen: () => {
                    const selectElement = document.getElementById("ModalListModel") as HTMLSelectElement;
                    this.dataModels.forEach((model) => {
                        const option = document.createElement("option");
                        option.value = model.id.toString();
                        option.textContent = model.name;
                        selectElement.appendChild(option);
                    });
                },
            })
            .then((result) => {
                if (result.value) {
                    this._create($("#ModalListTitle").val(), $("#ModalListModel").val(), result.dismiss || !this.acl.SYSPAD_PIM_LIST_UPDATE ? false : true);
                }
            });
    }

    /**
     * Create a list
     *
     * @param name
     * @param open
     */
    private _create(name, model, open: boolean = false) {
        if (name) {
            this._listcascadeService.createListCascade(name, model).subscribe(
                (data: any) => {
                    this._modalService.success(this._translateService.instant("general.created"));
                    if (open) {
                        this._router.navigate(["update", data.data.id], { relativeTo: this._route });
                    } else {
                        this._listcascadeService.getListCascades().subscribe((elements: any) => this._initList(elements.data));
                    }
                },
                (response) => {
                    response = response.error;
                    this._modalService.error(this._translateService.instant("response.error." + response.key + "." + response.method, { data: response.data }));
                }
            );
        }
    }

    /**
     * Init the list
     *
     * @param elementTypes
     */
    private _initList(listcascades: any) {
        const formatedData = this._tableDataPipe.transform(listcascades);
        this.columns = formatedData[0];
        this.table = formatedData[1];
    }

    onActionClick(event: ActionEvent) {
        const id = event.rowId;
        switch (event.action) {
            case ActionType.Delete:
                this._listcascadeService.deleteListCascade(id).subscribe(() => {
                    this._listcascadeService.getListCascades().subscribe((data) => {
                        this._lists = data.data;

                        this._initList(this._lists);
                    });
                });

                break;
            case ActionType.Update:
                this._router.navigate(["update", id], { relativeTo: this._route });
                break;
        }
    }

    onImport() {
        const infos = [this._translateService.instant("import.columnInfos.listcascade")];

        this._modalService.import("import-listcascade-base-test.xlsx", infos, "listcascade").then((fileData) => {
            if (fileData) {
                this._dataService.convertFile(fileData).subscribe((res: any) => {
                    const file = { filename: fileData.name, mimeType: fileData.type, size: fileData.size, base64: res };

                    this._listcascadeService.importFromFile(file).subscribe((res: any) => {
                        if (res.type === "success") {
                            this._toastService.show({ message: this._translateService.instant("import.succeeds"), type: "success" });
                            this._listcascadeService.getListCascades().subscribe((elements: any) => this._initList(elements.data));
                        }
                    });
                });
            }
        });
    }
}
